import React, { useCallback, useState } from "react";
import { ImageOff } from "lucide-react";
import { mediaUrl } from "../lib/api";

/**
 * Migrated imagery is wildly inconsistent: full-bleed workshop photography sits
 * next to 166x64 brand logos and white-background supplier cutouts. Cropping
 * them all to "cover" makes the cutouts read as harsh white rectangles on the
 * dark theme, and stretching a tiny logo to fill a card makes it blurry.
 *
 * So on load we classify the image from its own border pixels and pick a
 * treatment:
 *
 *   transparent border -> logo/graphic  : contain, padded, dark tile
 *   near-white border  -> supplier cutout: contain, padded, LIGHT tile so the
 *                                          cutout looks like a product tile
 *                                          instead of a white blowout
 *   anything else      -> real photograph: cover, fills the frame
 *
 * Small images are additionally capped so they are never upscaled into mush.
 * Everything is same-origin (/api/media/...) so the canvas is never tainted.
 */

const SAMPLE = 24;

function classify(img) {
  try {
    const c = document.createElement("canvas");
    c.width = SAMPLE;
    c.height = SAMPLE;
    const ctx = c.getContext("2d", { willReadFrequently: true });
    if (!ctx) return "photo";
    ctx.clearRect(0, 0, SAMPLE, SAMPLE);
    ctx.drawImage(img, 0, 0, SAMPLE, SAMPLE);
    const { data } = ctx.getImageData(0, 0, SAMPLE, SAMPLE);

    let n = 0;
    let clear = 0;
    let lum = 0;
    const at = (x, y) => (y * SAMPLE + x) * 4;
    const push = (i) => {
      const a = data[i + 3];
      n += 1;
      if (a < 24) {
        clear += 1;
        return;
      }
      lum += 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
    };

    for (let x = 0; x < SAMPLE; x += 1) {
      push(at(x, 0));
      push(at(x, SAMPLE - 1));
    }
    for (let y = 1; y < SAMPLE - 1; y += 1) {
      push(at(0, y));
      push(at(SAMPLE - 1, y));
    }

    if (clear / n > 0.6) return "logo";
    const opaque = n - clear;
    if (opaque > 0 && lum / opaque > 233) return "cutout";
    return "photo";
  } catch {
    return "photo";
  }
}

export default function SmartImage({
  src,
  alt = "",
  className = "",
  fit = "cover",
  ratio = "aspect-[16/10]",
  fallbackLabel,
  loading = "lazy",
  fill = false,
  smart = true,
  w = 800,
}) {
  const [failed, setFailed] = useState(false);
  const [kind, setKind] = useState(null);
  const [small, setSmall] = useState(false);

  const url = mediaUrl(src, w);
  // "aspect-auto" = masonry mode: keep the photo's own proportions.
  const natural = ratio === "aspect-auto";
  // fill = stretch to the parent box instead of holding an aspect ratio
  const frame = fill ? "absolute inset-0" : natural ? "aspect-[4/3]" : ratio;

  const onLoad = useCallback(
    (e) => {
      const img = e.currentTarget;
      if (!smart) return;
      setSmall(img.naturalWidth > 0 && img.naturalWidth < 420);
      setKind(classify(img));
    },
    [smart],
  );

  if (url && !failed && natural && !fill) {
    return (
      <div className={`relative overflow-hidden ${className}`} style={{ background: "rgba(255,255,255,0.03)" }}>
        <img
          src={url}
          alt={alt}
          loading={loading}
          onError={() => setFailed(true)}
          className="h-auto w-full object-cover transition-transform duration-500"
        />
      </div>
    );
  }

  if (!url || failed) {
    return (
      <div
        className={`flex items-center justify-center ${fill ? "absolute inset-0" : "relative"} ${frame} ${className}`}
        style={{ background: "linear-gradient(135deg, rgba(31,107,58,0.18), rgba(11,13,12,0.9))" }}
      >
        <div className="noise-overlay" />
        {fallbackLabel ? (
          <span className="max-w-full px-4 text-center text-xs font-bold uppercase leading-snug tracking-[0.14em] gold-text opacity-70">
            {String(fallbackLabel).slice(0, 42)}
          </span>
        ) : (
          <ImageOff className="h-6 w-6 text-[color:var(--nq-stone)] opacity-50" />
        )}
      </div>
    );
  }

  // Decide the treatment. Before the image loads we fall back to the caller's
  // `fit` so there is no layout shift.
  const contain = kind ? kind !== "photo" : fit === "contain";
  const tile =
    kind === "cutout"
      ? "linear-gradient(160deg, #F4F1EA 0%, #E4DED1 100%)"
      : "rgba(255,255,255,0.03)";

  return (
    <div
      className={`overflow-hidden ${fill ? "absolute inset-0" : "relative"} ${frame} ${className}`}
      style={{ background: tile }}
      data-image-kind={kind || "pending"}
    >
      <img
        src={url}
        alt={alt}
        loading={loading}
        onLoad={onLoad}
        onError={() => setFailed(true)}
        className={`transition-transform duration-500 ${
          contain
            ? `mx-auto h-full w-full object-contain ${kind === "logo" ? "p-6" : "p-4"}`
            : "h-full w-full object-cover"
        } ${small && contain ? "max-h-[68%] max-w-[68%] my-auto absolute inset-0" : ""}`}
      />
    </div>
  );
}
